import React, { useState, useEffect } from "react";
import ContainerDashboard from "../../components/layouts/ContainerDashboard";
import { axiosInstance } from "../Config";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Loader from "../Loader";

/* ── helpers ── */
const fmt = (n) => Number(n).toLocaleString("en-IN");

const PLAN_COLORS = [
  "#A44CD7",
  "#0CACC2",
  "#F55145",
  "#F9BE22",
  "#8A3BBD",
  "#19C37D",
  "#5A79DF",
  "#EC2434",
];

/* ─────────────────────────────────────────
   KID PLANS TAB
───────────────────────────────────────── */
const KidPlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("kid-plans");
      setPlans(res.data?.data ?? []);
    } catch {
      toast.error("Failed to load kid plans.");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingPlan(null);
    setName("");
    setDescription("");
    setAmount("");
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setName(plan.name);
    setDescription(plan.description || "");
    setAmount(String(plan.amount));
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (amount === "" || isNaN(amount) || Number(amount) < 0)
      e.amount = "Valid amount is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const body = { name, description, amount: Number(amount) };
      if (editingPlan) {
        const res = await axiosInstance.put(
          `kid-plans/${editingPlan.id}`,
          body,
        );
        setPlans((prev) =>
          prev.map((p) =>
            p.id === editingPlan.id ? res.data?.data ?? { ...p, ...body } : p,
          ),
        );
        toast.success("Kid plan updated!");
      } else {
        const res = await axiosInstance.post("kid-plans", body);
        setPlans((prev) => [...prev, res.data?.data]);
        toast.success("Kid plan created!");
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (plan) => {
    swal({
      title: "Delete Plan?",
      text: `"${plan.name}" will be removed.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      setLoading(true);
      try {
        await axiosInstance.delete(`kid-plans/${plan.id}`);
        setPlans((prev) => prev.filter((p) => p.id !== plan.id));
        swal("", "Plan deleted successfully.", "success");
      } catch {
        swal("", "Failed to delete plan.", "error");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <>
      {loading && <Loader />}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
          {plans.length} plan{plans.length !== 1 ? "s" : ""}
        </p>
        <button className="button btn-green color-white" onClick={openAdd}>
          <i className="bi bi-plus-circle me-2"></i>Add Kid Plan
        </button>
      </div>

      <div className="table-responsive">
        <table className="jean-table table">
          <tbody>
            {plans.map((plan, idx) => (
              <tr key={plan.id}>
                <td width={46}>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 40,
                      height: 40,
                      background: PLAN_COLORS[idx % PLAN_COLORS.length],
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    <i className="bi bi-star-fill" style={{ fontSize: 14 }}></i>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Plan Name
                    </p>
                    <p className="mb-0 fw-bold">{plan.name}</p>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Amount
                    </p>
                    <p
                      className="mb-0 fw-bold"
                      style={{ color: "var(--primary)" }}
                    >
                      ₹{fmt(plan.amount)}
                    </p>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Description
                    </p>
                    <p className="mb-0">
                      {plan.description || (
                        <span className="text-muted fst-italic">—</span>
                      )}
                    </p>
                  </div>
                </td>
                <td className="pe-3">
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-sm fw-semibold"
                      style={{
                        background:
                          "linear-gradient(135deg, var(--btn-primary), var(--btn-secondary))",
                        color: "#fff",
                        borderRadius: 8,
                        padding: "6px 14px",
                        fontSize: 13,
                      }}
                      onClick={() => openEdit(plan)}
                    >
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                    <button
                      className="btn btn-sm fw-semibold"
                      style={{
                        background: "#fff0f0",
                        color: "#e53935",
                        border: "1px solid #ffd0d0",
                        borderRadius: 8,
                        padding: "6px 14px",
                        fontSize: 13,
                      }}
                      onClick={() => handleDelete(plan)}
                    >
                      <i className="bi bi-trash me-1"></i>Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {plans.length === 0 && !loading && (
              <tr>
                <td colSpan={5}>
                  <div className="alert alert-success ps-4">
                    No kid plans found!
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div
          className="tutor-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="tutor-modal">
            <div className="tutor-modal-header">
              <h5 className="mb-0">
                <i
                  className={`bi bi-${
                    editingPlan ? "pencil-square" : "plus-circle"
                  } me-2`}
                ></i>
                {editingPlan ? "Edit Kid Plan" : "Add Kid Plan"}
              </h5>
              <button
                className="tutor-modal-close"
                onClick={() => setShowModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="tutor-modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Plan Name <i className="text-danger">*</i>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Basic, Premium"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Amount (₹) <i className="text-danger">*</i>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="e.g. 499"
                    value={amount}
                    min="0"
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  {errors.amount && (
                    <small className="text-danger">{errors.amount}</small>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Plan description..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
              <div
                className="d-flex justify-content-end gap-2 pt-3 mt-3"
                style={{ borderTop: "1px solid #f0f0f0" }}
              >
                <button
                  type="button"
                  className="button"
                  style={{
                    background: "#6c757d",
                    color: "#fff",
                    borderRadius: "10px",
                  }}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button btn-green color-white"
                  disabled={saving}
                >
                  <i
                    className={`bi bi-${
                      saving ? "arrow-repeat rotation" : "check-circle"
                    } me-2`}
                  ></i>
                  {saving ? "Saving..." : editingPlan ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────
   TUTOR PLANS TAB
───────────────────────────────────────── */
const TutorPlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [kidPlans, setKidPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [sessions, setSessions] = useState("");
  const [freePlan, setFreePlan] = useState("");
  const [freePlanMonths, setFreePlanMonths] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchKidPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("tutor-plans");
      setPlans(res.data?.data ?? []);
    } catch {
      toast.error("Failed to load tutor plans.");
    } finally {
      setLoading(false);
    }
  };

  const fetchKidPlans = async () => {
    try {
      const res = await axiosInstance.get("kid-plans");
      setKidPlans(res.data?.data ?? []);
    } catch {}
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setAmount("");
    setSessions("");
    setFreePlan("");
    setFreePlanMonths("");
    setErrors({});
  };

  const openAdd = () => {
    setEditingPlan(null);
    resetForm();
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setName(plan.name);
    setDescription(plan.description || "");
    setAmount(String(plan.amount));
    setSessions(String(plan.sessions || ""));
    setFreePlan(plan.freePlan ? String(plan.freePlan) : "");
    setFreePlanMonths(plan.freePlanMonths ? String(plan.freePlanMonths) : "");
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (amount === "" || isNaN(amount) || Number(amount) < 0)
      e.amount = "Valid amount is required";
    if (!sessions || isNaN(sessions) || Number(sessions) <= 0)
      e.sessions = "Valid sessions count is required";
    if (freePlan && (!freePlanMonths || Number(freePlanMonths) <= 0))
      e.freePlanMonths = "Months required when a free plan is selected";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        name,
        description,
        amount: Number(amount),
        sessions: Number(sessions),
        ...(freePlan
          ? {
              freePlan: Number(freePlan),
              freePlanMonths: Number(freePlanMonths),
            }
          : {}),
      };
      if (editingPlan) {
        const res = await axiosInstance.put(
          `tutor-plans/${editingPlan.id}`,
          body,
        );
        setPlans((prev) =>
          prev.map((p) =>
            p.id === editingPlan.id ? res.data?.data ?? { ...p, ...body } : p,
          ),
        );
        toast.success("Tutor plan updated!");
      } else {
        const res = await axiosInstance.post("tutor-plans", body);
        setPlans((prev) => [...prev, res.data?.data]);
        toast.success("Tutor plan created!");
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (plan) => {
    swal({
      title: "Delete Plan?",
      text: `"${plan.name}" will be removed.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      setLoading(true);
      try {
        await axiosInstance.delete(`tutor-plans/${plan.id}`);
        setPlans((prev) => prev.filter((p) => p.id !== plan.id));
        swal("", "Plan deleted successfully.", "success");
      } catch {
        swal("", "Failed to delete plan.", "error");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <>
      {loading && <Loader />}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
          {plans.length} plan{plans.length !== 1 ? "s" : ""}
        </p>
        <button className="button btn-green color-white" onClick={openAdd}>
          <i className="bi bi-plus-circle me-2"></i>Add Tutor Plan
        </button>
      </div>

      <div className="table-responsive">
        <table className="jean-table table">
          <tbody>
            {plans.map((plan, idx) => (
              <tr key={plan.id}>
                <td width={46}>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 40,
                      height: 40,
                      background: PLAN_COLORS[idx % PLAN_COLORS.length],
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="bi bi-person-video3"
                      style={{ fontSize: 14 }}
                    ></i>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Plan Name
                    </p>
                    <p className="mb-0 fw-bold">{plan.name}</p>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Amount
                    </p>
                    <p
                      className="mb-0 fw-bold"
                      style={{ color: "var(--primary)" }}
                    >
                      ₹{fmt(plan.amount)}
                    </p>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Sessions
                    </p>
                    <p className="mb-0">{plan.sessions}</p>
                  </div>
                </td>
                <td>
                  <div className="therapist-info">
                    <p
                      className="mb-1"
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#999",
                      }}
                    >
                      Description
                    </p>
                    <p className="mb-0">
                      {plan.description || (
                        <span className="text-muted fst-italic">—</span>
                      )}
                    </p>
                  </div>
                </td>
                <td>
                  {plan.freePlanDetails ? (
                    <span
                      className="plan-free-badge"
                      style={{ display: "inline-flex" }}
                    >
                      <i className="bi bi-gift me-1"></i>
                      {` Includes ${plan.freePlanDetails.name} for ${
                        plan.freePlanMonths
                      } month${plan.freePlanMonths > 1 ? "s" : ""}`}
                    </span>
                  ) : (
                    <span className="text-muted" style={{ fontSize: 13 }}>
                      —
                    </span>
                  )}
                </td>
                <td className="pe-3">
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-sm fw-semibold"
                      style={{
                        background:
                          "linear-gradient(135deg, var(--btn-primary), var(--btn-secondary))",
                        color: "#fff",
                        borderRadius: 8,
                        padding: "6px 14px",
                        fontSize: 13,
                      }}
                      onClick={() => openEdit(plan)}
                    >
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                    <button
                      className="btn btn-sm fw-semibold"
                      style={{
                        background: "#fff0f0",
                        color: "#e53935",
                        border: "1px solid #ffd0d0",
                        borderRadius: 8,
                        padding: "6px 14px",
                        fontSize: 13,
                      }}
                      onClick={() => handleDelete(plan)}
                    >
                      <i className="bi bi-trash me-1"></i>Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {plans.length === 0 && !loading && (
              <tr>
                <td colSpan={7}>
                  <div className="alert alert-success ps-4">
                    No tutor plans found!
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div
          className="tutor-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="tutor-modal" style={{ maxWidth: 800 }}>
            <div className="tutor-modal-header">
              <h5 className="mb-0">
                <i
                  className={`bi bi-${
                    editingPlan ? "pencil-square" : "plus-circle"
                  } me-2`}
                ></i>
                {editingPlan ? "Edit Tutor Plan" : "Add Tutor Plan"}
              </h5>
              <button
                className="tutor-modal-close"
                onClick={() => setShowModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="tutor-modal-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">
                    Plan Name <i className="text-danger">*</i>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Gold, Platinum"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">
                    Amount (₹) <i className="text-danger">*</i>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="e.g. 2999"
                    value={amount}
                    min="0"
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  {errors.amount && (
                    <small className="text-danger">{errors.amount}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">
                    Number of Sessions <i className="text-danger">*</i>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="e.g. 10"
                    value={sessions}
                    min={1}
                    onChange={(e) => setSessions(e.target.value)}
                  />
                  {errors.sessions && (
                    <small className="text-danger">{errors.sessions}</small>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Plan description..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                {/* Free Plan section */}
                <div className="col-12">
                  <div className="plan-free-section">
                    <p className="plan-free-section-label">
                      <i className="bi bi-gift me-2"></i>Free Kid Plan
                      (Optional)
                    </p>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label
                          className="form-label fw-semibold"
                          style={{ fontSize: 13 }}
                        >
                          Linked Kid Plan
                        </label>
                        <select
                          className="form-control"
                          value={freePlan}
                          onChange={(e) => {
                            setFreePlan(e.target.value);
                            if (!e.target.value) setFreePlanMonths("");
                          }}
                        >
                          <option value="">None</option>
                          {kidPlans.map((kp) => (
                            <option key={kp.id} value={kp.id}>
                              {kp.name} — ₹{fmt(kp.amount)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label
                          className="form-label fw-semibold"
                          style={{ fontSize: 13 }}
                        >
                          Duration (months)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="e.g. 3"
                          value={freePlanMonths}
                          min={1}
                          disabled={!freePlan}
                          onChange={(e) => setFreePlanMonths(e.target.value)}
                        />
                        {errors.freePlanMonths && (
                          <small className="text-danger">
                            {errors.freePlanMonths}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="d-flex justify-content-end gap-2 pt-3 mt-3"
                style={{ borderTop: "1px solid #f0f0f0" }}
              >
                <button
                  type="button"
                  className="button"
                  style={{
                    background: "#6c757d",
                    color: "#fff",
                    borderRadius: "10px",
                  }}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button btn-green color-white"
                  disabled={saving}
                >
                  <i
                    className={`bi bi-${
                      saving ? "arrow-repeat rotation" : "check-circle"
                    } me-2`}
                  ></i>
                  {saving ? "Saving..." : editingPlan ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
const PlansSubscriptions = () => {
  const [activeTab, setActiveTab] = useState("kids");

  useEffect(() => {
    document.title = "Plans & Subscriptions | Mindora";
    window.scrollTo(0, 0);
  }, []);

  return (
    <ContainerDashboard
      title="Plans & Subscriptions"
      description="Manage kid and tutor subscription plans"
    >
      <section className="ps-dashboards">
        <div
          className="p-4 py-5 bg-gradientt rounded-4"
          style={{ minHeight: "75vh" }}
        >
          {/* Tab switcher */}
          <div className="plans-tab-bar mb-4">
            <button
              className={`plans-tab-btn ${
                activeTab === "kids" ? "active" : ""
              }`}
              onClick={() => setActiveTab("kids")}
            >
              <i className="bi bi-star me-2"></i>Kids Plans
            </button>
            <button
              className={`plans-tab-btn ${
                activeTab === "tutor" ? "active" : ""
              }`}
              onClick={() => setActiveTab("tutor")}
            >
              <i className="bi bi-person-video3 me-2"></i>Tutor Plans
            </button>
          </div>

          {activeTab === "kids" && <KidPlansTab />}
          {activeTab === "tutor" && <TutorPlansTab />}
        </div>
      </section>
    </ContainerDashboard>
  );
};

export default PlansSubscriptions;
